// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Form } from "react-bootstrap";

import { DataState } from "types";
import { formatBytes, isSet } from "utils";
import { toDropdownItems } from "guiutils";
import { IState } from "states/states";
import {
  ActionType,
  IAction,
  raiseAction,
  IActionTypeConfirmPopup,
  IActionSaveControllerData,
} from "actions";
import { IStateController } from "states/controllerstates";

import FormattedDate from "view/comp/FormattedDate";
import FormattedTime from "view/comp/FormattedTime";
import SaveFeedback from "view/comp/SaveFeedback";
import Dropdown from "view/comp/Dropdown";

type ILocalSettingsProps = IStateController & {
  epoch: number;
  isCloudEnabled: boolean;
  isAdminUser: boolean;
};

const mapStateToProps = (state: IState) => {
  const props: ILocalSettingsProps = {
    ...(state.controller ?? {
      dataState: DataState.EMPTY,
    }),
    epoch: Math.round(Date.now() / 1000),
    isCloudEnabled: state.parameter?.general.cloudEnable ?? false,
    isAdminUser: state.site.user?.secId === "admin",
  };
  return props;
};

interface ILocalSettingsState {
  isEditing: boolean;
  needsReboot?: boolean;
  hostname?: string;
  ipMode?: "dhcp" | "static";
  ipAddress?: string;
  netmask?: string;
  gateway?: string;
  dnsServer?: string;
  operatorPassword?: string;
  operatorPassword2?: string;
  swBranch?: string;
}

interface ILocalSettingsDispatch {
  onLogin: () => void;
  onConfirmReboot: (doPoweroffOnly: boolean, title: string, message: string) => void;
  onSave: (data: IActionSaveControllerData) => void;
  onResetSaveState: () => void;
  onWarningMessage: (title: string, message: string) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ILocalSettingsDispatch = {
    onLogin: () => {
      dispatch(raiseAction(ActionType.ACT_GOTO_LOGIN));
    },
    onConfirmReboot: (doPoweroffOnly: boolean, title: string, message: string) => {
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
        action: ActionType.ACT_REBOOT_REQ,
        actionPayload: doPoweroffOnly,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
    onSave: (data: IActionSaveControllerData) => {
      dispatch(raiseAction(ActionType.ACT_SAVE_CONTROLLERDATA_REQ, data));
    },
    onResetSaveState: () => {
      dispatch(raiseAction(ActionType.ACT_SAVE_CHARGER_POWER_RESET));
    },
    onWarningMessage: (title: string, message: string) => {
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  confirmRebootTitle: {
    id: "Rebooting System",
  },
  confirmRebootBody: {
    id: "This will perform a reboot of this controller. The energy regulation and recording will not be available during this time. Are you sure?",
  },
  confirmPoweroffTitle: {
    id: "Powering Off System",
  },
  confirmPoweroffBody: {
    id: "This will power-off this controller. The controller will be unusable until you disconnect and reconnect its power supply. Are you sure?",
  },
  iptypeCaption: {
    id: "Select IP Mode",
  },
  confirmPasswordTitle: {
    id: "Change Password",
  },
  confirmOperatorPasswordBody: {
    id: "The entered operator passwords are not equal. Aborting change.",
  },
  confirmHostnameTitle: {
    id: "Change Hostname",
  },
  confirmHostnameBody: {
    id: "The hostname may only contain lowercase letters, digits and the hyphen ('-') character. Aborting change.",
  },
});

class LocalSettingsComponent extends React.Component<
  ILocalSettingsProps & ILocalSettingsDispatch & WrappedComponentProps,
  ILocalSettingsState
> {
  public state: ILocalSettingsState = { isEditing: false };

  public render() {
    return (
      <div id="localsettings">
        {this.renderStatus()}
        {this.renderControllerButtonBox()}
        {this.renderIpConfig()}
        {this.state.isEditing && this.renderUserPassword()}
        {this.renderEditButtons()}
      </div>
    );
  }

  private renderStatus() {
    return (
      <div className="mb-3">
        <h1>
          <FormattedMessage id="Controller Status" />
        </h1>
        <table className="datatable">
          <tbody>
            <tr>
              <th style={{ width: "50%" }}>
                <FormattedMessage id="Date and Time" />
              </th>
              <td style={{ width: "50%" }}>
                <FormattedDate valueMs={this.props.epoch * 1000} />{" "}
                <FormattedTime valueMs={this.props.epoch * 1000} />
              </td>
            </tr>
            <tr>
              <th>
                <FormattedMessage id="Serial Number" />
              </th>
              <td>{this.props.serialNumber}</td>
            </tr>
            <tr>
              <th>
                <FormattedMessage id="Software Version" />
              </th>
              <td>
                {this.state.isEditing ? (
                  <Form.Control
                    type="text"
                    value={this.state.swBranch ?? this.props.swVersion?.split(" / ").pop() ?? ""}
                    onChange={(event) => {
                      this.setState({
                        swBranch: event?.currentTarget.value,
                      });
                    }}
                  />
                ) : (
                  <>{this.props.swVersion}</>
                )}
              </td>
            </tr>
            <tr>
              <th>
                <FormattedMessage id="Cloud Status" />
              </th>
              <td>
                {!this.props.isCloudEnabled ? (
                  <FormattedMessage id="Disabled" />
                ) : this.props.isCloudConnected ? (
                  <>
                    <FormattedMessage id="Connected" />, {this.props.cloudListener}{" "}
                    <FormattedMessage id="listener" />
                  </>
                ) : (
                  <>
                    <FormattedMessage id="Disconnected" />: {this.props.cloudLastMessage}
                  </>
                )}
              </td>
            </tr>
            <tr>
              <th>
                <FormattedMessage id="RS485 Bus Load, Controller Temperature" />
              </th>
              <td>
                {isSet(this.props.modbusLoad) ? `${Math.round(this.props.modbusLoad)} %` : "-"},{" "}
                {this.props.temperature ? `${Math.round(this.props.temperature)} °C` : "-"}
              </td>
            </tr>
            <tr>
              <th>
                <FormattedMessage id="CPU Load / Free RAM / Free Storage" />
              </th>
              <td>
                {isSet(this.props.cpuLoad) ? `${Math.round(this.props.cpuLoad)} %` : "-"} /{" "}
                {formatBytes(this.props.freeMem, 1)} / {formatBytes(this.props.freeFlash, 1)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  private renderControllerButtonBox() {
    return (
      <div className="buttonbox" style={{ marginTop: -10, marginBottom: 20 }}>
        <Button
          id="reload"
          variant="secondary"
          type="submit"
          onClick={(_evt) => window.location.reload()}
        >
          <FormattedMessage id="Reload Site" />
        </Button>
        {this.props.isAdminUser && (
          <Button
            variant="danger"
            id="reboot"
            type="submit"
            onClick={(_evt) =>
              this.props.onConfirmReboot(
                false,
                this.props.intl.formatMessage(messages.confirmRebootTitle),
                this.props.intl.formatMessage(messages.confirmRebootBody),
              )
            }
          >
            <FormattedMessage id="Reboot Controller" />
          </Button>
        )}
        {this.props.isAdminUser && (
          <Button
            variant="danger"
            id="poweroff"
            type="submit"
            onClick={(_evt) =>
              this.props.onConfirmReboot(
                true,
                this.props.intl.formatMessage(messages.confirmPoweroffTitle),
                this.props.intl.formatMessage(messages.confirmPoweroffBody),
              )
            }
          >
            <FormattedMessage id="Power-Off Controller" />
          </Button>
        )}
      </div>
    );
  }

  private renderIpConfig() {
    const ipTypeItems = toDropdownItems(
      ["Static", "DHCP"],
      "wiredIpType",
      (name, value) => this.setState({ ipMode: value.toLowerCase() as "dhcp" | "static" }),
      (value) => value,
    );

    return (
      <div className="mb-3">
        <h1>
          <FormattedMessage id="IP Configuration" />
        </h1>
        <table className="datatable">
          <tbody>
            <tr>
              <th style={{ width: "50%" }}>
                <FormattedMessage id="Hostname" />
              </th>
              <td style={{ width: "50%" }}>
                {this.state.isEditing ? (
                  <Form.Control
                    value={this.state.hostname ?? this.props.hostname}
                    onChange={(event) => {
                      this.setState({
                        hostname: event?.currentTarget.value,
                      });
                    }}
                  />
                ) : (
                  this.props.hostname
                )}
              </td>
            </tr>
            <tr>
              <th>
                <FormattedMessage id="Current IP Addresses" />
              </th>
              <td>{this.props.ipAddress}</td>
            </tr>
            {this.state.isEditing && (
              <>
                <tr>
                  <th>
                    <FormattedMessage id="Mode" />
                  </th>
                  <td id="ipMode">
                    <Dropdown
                      items={ipTypeItems}
                      caption={this.props.intl.formatMessage(messages.iptypeCaption)}
                      curItem={
                        this.state.ipMode ?? this.props.intl.formatMessage(messages.iptypeCaption)
                      }
                    />
                  </td>
                </tr>
                {this.state.ipMode === "static" && (
                  <>
                    <tr>
                      <th>
                        <FormattedMessage id="Static IP Address" />:
                      </th>
                      <td>
                        <Form.Control
                          value={this.state.ipAddress ?? ""}
                          onChange={(event) => {
                            this.setState({
                              ipAddress: event?.currentTarget.value,
                            });
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <FormattedMessage id="Netmask" />:
                      </th>
                      <td>
                        <Form.Control
                          value={this.state.netmask ?? ""}
                          onChange={(event) => {
                            this.setState({
                              netmask: event?.currentTarget.value,
                            });
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <FormattedMessage id="Default Gateway" />:
                      </th>
                      <td>
                        <Form.Control
                          value={this.state.gateway ?? ""}
                          onChange={(event) => {
                            this.setState({
                              gateway: event?.currentTarget.value,
                            });
                          }}
                        />
                      </td>
                    </tr>
                  </>
                )}
              </>
            )}
            <tr>
              <th>
                <FormattedMessage id="DNS Server" />
              </th>
              <td>
                {this.state.isEditing && this.state.ipMode === "static" ? (
                  <Form.Control
                    value={this.state.dnsServer ?? ""}
                    onChange={(event) => {
                      this.setState({
                        dnsServer: event?.currentTarget.value,
                      });
                    }}
                  />
                ) : (
                  this.props.dnsServer
                )}
              </td>
            </tr>
            {!this.state.isEditing && (
              <tr>
                <th>
                  <FormattedMessage id="IP Settings" />
                </th>
                <td>{this.props.ipSettings}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  private renderUserPassword() {
    return (
      <div className="mb-3">
        <h1>
          <FormattedMessage id="Other Configuration" />
        </h1>
        <table className="datatable">
          <tbody>
            <tr>
              <th style={{ width: "50%" }}>
                <FormattedMessage id="Operator Password" />
              </th>
              <td style={{ width: "50%" }}>
                <Form.Control
                  type="password"
                  value={this.state.operatorPassword ?? ""}
                  onChange={(event) => {
                    this.setState({
                      operatorPassword: event?.currentTarget.value,
                    });
                  }}
                />
              </td>
            </tr>
            <tr>
              <th>
                <FormattedMessage id="Repeat Operator Password" />
              </th>
              <td>
                <Form.Control
                  type="password"
                  value={this.state.operatorPassword2 ?? ""}
                  onChange={(event) => {
                    this.setState({
                      operatorPassword2: event?.currentTarget.value,
                    });
                  }}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  private renderEditButtons() {
    if (this.props.isAdminUser) {
      return (
        <div>
          <Button
            variant="secondary"
            className="me-2"
            onClick={() => {
              if (this.state.isEditing) {
                this.setState({
                  isEditing: false,
                });
                this.props.onResetSaveState();
              } else {
                // reset whole state here
                this.setState({
                  isEditing: true,
                  hostname: undefined,
                  ipMode: undefined,
                  ipAddress: undefined,
                  netmask: undefined,
                  gateway: undefined,
                  dnsServer: undefined,
                  operatorPassword: undefined,
                  operatorPassword2: undefined,
                  swBranch: undefined,
                });
              }
            }}
          >
            {this.state.isEditing ? (
              <FormattedMessage id="Cancel Edit" />
            ) : (
              <FormattedMessage id="Edit Settings" />
            )}
          </Button>
          {this.state.isEditing && (
            <Button
              variant="primary"
              className="me-2"
              onClick={() => {
                this.onSave();
              }}
            >
              <FormattedMessage id="Save Settings" />
            </Button>
          )}
          {this.state.isEditing && (
            <SaveFeedback dataState={this.props.dataState} className="mt-2" />
          )}
          {this.state.needsReboot === true && (
            <div className={`alert alert-warning`}>
              <FormattedMessage id="Reboot needed: Please reboot the device after changing the IP configuration." />{" "}
              <FormattedMessage id="Afterwards wait a few minutes and then reload this web side." />
            </div>
          )}
        </div>
      );
    }
    // login needed
    return (
      <div id="loginlink">
        <FormattedMessage id="To modify the settings you need to login as administrator:" />
        <span tabIndex={0} className="text-link ms-2" onClick={this.props.onLogin}>
          <FormattedMessage id="Login" />
        </span>
      </div>
    );
  }

  private onSave() {
    // check for matching passwords
    if (
      this.state.operatorPassword &&
      this.state.operatorPassword !== this.state.operatorPassword2
    ) {
      this.props.onWarningMessage(
        this.props.intl.formatMessage(messages.confirmPasswordTitle),
        this.props.intl.formatMessage(messages.confirmOperatorPasswordBody),
      );
      return; // abort save
    }

    // check for valid hostname
    if (this.state.hostname && !/^[a-z-0-9]+$/.test(this.state.hostname)) {
      this.props.onWarningMessage(
        this.props.intl.formatMessage(messages.confirmHostnameTitle),
        this.props.intl.formatMessage(messages.confirmHostnameBody),
      );
      return; // abort save
    }

    // check for changed IP settings
    if (
      this.state.ipMode ||
      this.state.ipAddress ||
      this.state.netmask ||
      this.state.hostname ||
      this.state.gateway ||
      this.state.dnsServer
    ) {
      // indicate reboot need
      this.setState({ needsReboot: true });
    }
    const { isEditing, needsReboot, operatorPassword2, ...data } = this.state;
    this.props.onSave(data);
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(LocalSettingsComponent));
